import { useEffect, useState, type ReactElement } from "react";
import { OWNER_ROLE_ID } from "@shared/protocol";
import type { Role, User } from "@shared/types";
import { C, FONT_MONO, hexA, initialOf, statusColor } from "@/theme";
import { colorOf, roleOf, serverMembers, useApp } from "@/state/store";
import { HeadphonesIcon, MicIcon } from "@/components/icons";

type MemberGroup = { role: Role; members: User[] };

/** Foto do membro; sem foto (ou se ela falhar) cai na inicial colorida do desenho. */
function Avatar({
  url,
  name,
  color,
}: {
  url: string | null;
  name: string;
  color: string;
}): ReactElement {
  const [broken, setBroken] = useState(false);

  // Uma foto nova precisa de uma nova chance de carregar.
  useEffect(() => {
    setBroken(false);
  }, [url]);

  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setBroken(true)}
        style={{
          width: 30,
          height: 30,
          display: "block",
          borderRadius: "50%",
          objectFit: "cover",
          background: hexA(color, 0.18),
        }}
      />
    );
  }
  return (
    <span
      style={{
        width: 30,
        height: 30,
        display: "grid",
        placeItems: "center",
        borderRadius: "50%",
        fontSize: 12,
        fontWeight: 600,
        background: hexA(color, 0.18),
        color,
      }}
    >
      {initialOf(name)}
    </span>
  );
}

export function MembersPanel() {
  const { state, actions } = useApp();

  // O painel so existe ao lado de um canal de texto, como no desenho.
  const isChannel = state.activeKey.startsWith("ch:");
  if (!state.membersOpen || !isChannel) return null;

  // Só quem está NESTE servidor. Antes era `state.users`, que hoje guarda
  // todo mundo que eu enxergo — amigos de fora deste servidor inclusive.
  const daqui = serverMembers(state);
  const ordered = [...state.roles].sort((a, b) => a.position - b.position);
  const groups: MemberGroup[] = ordered
    .map(role => ({
      role,
      members: daqui.filter(user => roleOf(state, user.id)?.id === role.id),
    }))
    .filter(group => group.members.length > 0);

  return (
    <div
      style={{
        width: 250,
        flex: "0 0 auto",
        overflowY: "auto",
        borderLeft: `1px solid ${C.line}`,
        background: C.panel,
        padding: "14px 10px",
      }}
    >
      {groups.map(group => (
        <div key={group.role.id} style={{ marginBottom: 14 }}>
          <div
            style={{
              margin: "2px 8px 6px",
              fontFamily: FONT_MONO,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.14em",
              color: C.textLabelDim,
            }}
          >
            {`${group.role.name.toUpperCase()} — ${group.members.length}`}
          </div>
          {group.members.map(member => {
            const color = colorOf(state, member.id);
            return (
              <button
                key={member.id}
                type="button"
                className="hov-bg-1f"
                onClick={() => actions.openProfile(member.id)}
                onContextMenu={event => {
                  event.preventDefault();
                  actions.openContextMenu(member.id, member.id, event.clientX, event.clientY);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 8px",
                  border: 0,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  background: "transparent",
                }}
              >
                <span style={{ position: "relative", width: 30, height: 30, flex: "0 0 auto" }}>
                  <Avatar url={member.avatarUrl} name={member.name} color={color} />
                  <span
                    style={{
                      position: "absolute",
                      right: -1,
                      bottom: -1,
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      border: `2px solid ${C.panel}`,
                      background: statusColor(member.status),
                    }}
                  />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 13.5,
                      fontWeight: 600,
                      color:
                        group.role.id === OWNER_ROLE_ID ? "var(--ac, #1F4FD8)" : C.textStrong,
                    }}
                  >
                    {member.name}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontFamily: member.customStatus ? undefined : FONT_MONO,
                      fontSize: member.customStatus ? 11.5 : 9.5,
                      color: member.customStatus ? C.textMuted : C.textLabel,
                      marginTop: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {member.customStatus || `@${member.handle}`}
                  </span>
                </span>
                <span style={{ flex: 1 }} />
                {member.serverMuted && (
                  <span
                    title="Silenciado no servidor"
                    style={{
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: hexA(C.red, 0.12),
                    }}
                  >
                    <MicIcon size={12} bare color={C.red} />
                  </span>
                )}
                {member.serverDeafened && (
                  <span
                    title="Áudio desativado no servidor"
                    style={{
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: hexA(C.amber, 0.12),
                      marginLeft: member.serverMuted ? 4 : 0,
                    }}
                  >
                    <HeadphonesIcon size={12} color={C.amber} off cutColor={C.amber} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
